/**
 * Onboarding API Endpoints
 *
 * The chat-window recipe framework IS onboarding (#4420): `completeOnboardingSession`
 * + `recordOnboardingProgress` (the recipe session + `onboarding_completed`
 * latch), the `syncStatus` readiness poll, and the briefing-preference toggle.
 * The user-level 5-state machine (`advance` + the email-summary / extract-contacts
 * helpers) was retired with the `platform.users.onboarding_state` column.
 */

import { http } from '@/lib/http'
import { getActiveWorkspaceId } from '@/lib/api/workspace-context'
import type {
  OnboardingSessionComplete,
  OnboardingProgressRequest,
} from '@xavior/contracts'

function wsOnboarding(sub = ''): string {
  return `/workspaces/${getActiveWorkspaceId()}/onboarding${sub}`
}

/**
 * `POST /onboarding/session/complete` — the recipe framework's completion latch.
 * Stamps the recipe session's `completed_at`, sets the `onboarding_completed`
 * user latch (read by jwt-auth as `onboardingComplete`), and mirrors completion
 * to the active workspace membership. Called by the recipe `onComplete`; the
 * caller then refreshes the session so `profile.onboardingComplete` flips true.
 */
export async function completeOnboardingSession(
  authToken: string | null,
): Promise<OnboardingSessionComplete> {
  // Use the workspace-scoped path (the router is mounted at BOTH /onboarding and
  // /workspaces/:id/onboarding) so the handler resolves workspaceId from the URL
  // param rather than the X-Workspace-Id header. The completion handler's
  // membership mirror (setMembershipOnboarding) is gated on a present workspaceId,
  // so a missing/late header would silently skip it and leave the membership at
  // 'pending' while the user latch flips — the URL param closes that gap (#4598
  // follow-up), matching the sibling syncStatus/kickoffSync calls.
  const response = await http.post<OnboardingSessionComplete>(
    wsOnboarding('/session/complete'),
    {},
    { authToken },
  )
  return response.data
}

/**
 * `POST /onboarding/session/progress` — record mid-flow step progress so the
 * recipe RESUMES after the connect-OAuth redirect (the page unloads) or a
 * mid-onboarding tab close. Best-effort: callers should `.catch()` and never
 * block the UI on it. Dedup-appends `completedStepId` and sets `current`.
 */
export async function recordOnboardingProgress(
  authToken: string | null,
  req: OnboardingProgressRequest,
): Promise<void> {
  await http.post('/onboarding/session/progress', req, { authToken })
}

export async function kickoffSync(authToken: string | null): Promise<void> {
  await http.post(wsOnboarding('/kickoff-sync'), {}, { authToken })
}

export interface SyncStatus {
  emailMessages: number
  calendarEvents: number
  driveFiles: number
  isReady: boolean
}

export async function syncStatus(authToken: string | null): Promise<SyncStatus> {
  const response = await http.get<SyncStatus>(wsOnboarding('/sync-status'), { authToken })
  return response.data
}

/**
 * One background discovery job's status as reported by
 * `GET /workspaces/:id/onboarding/discovery-status`. The API flattens the
 * job row's `result` JSONB into the entry, so a terminal `letter` job carries
 * its result fields inline — `revealText` (the personalized reveal letter)
 * and `empty` (true when the account was too quiet and the text is the
 * honest empty-state fallback, not a real profile).
 */
export interface DiscoveryJobStatus {
  status: 'pending' | 'running' | 'done' | 'failed' | 'deferred'
  revealText?: string
  empty?: boolean
  [key: string]: unknown
}

export interface DiscoveryStatus {
  jobs: {
    inbox?: DiscoveryJobStatus
    sent?: DiscoveryJobStatus
    calendar?: DiscoveryJobStatus
    letter?: DiscoveryJobStatus
  }
  allDone: boolean
}

/**
 * `GET /workspaces/:id/onboarding/discovery-status` — status of the 4
 * background discovery jobs (inbox, sent, calendar, letter) kicked off after
 * connect. Polled by the chat-window onboarding to surface the reveal letter
 * when `jobs.letter` lands (#5226).
 */
export async function discoveryStatus(authToken: string | null): Promise<DiscoveryStatus> {
  const response = await http.get<DiscoveryStatus>(wsOnboarding('/discovery-status'), { authToken })
  return response.data
}

/** `POST /onboarding/briefing-preference` — toggle the daily-briefing flag. */
export async function setBriefingPreference(
  authToken: string | null,
  enabled: boolean,
): Promise<void> {
  await http.post('/onboarding/briefing-preference', { enabled }, { authToken })
}

/**
 * `POST /workspaces/:id/onboarding/restart-discovery` — clear stale discovery
 * results from a prior demo run and kick a fresh real-data scan so personalized
 * beats reflect the user's actual world (not the demo persona's data, #3509).
 */
export async function restartDiscovery(authToken: string | null): Promise<void> {
  await http.post(wsOnboarding('/restart-discovery'), {}, { authToken })
}

export const onboarding = {
  completeOnboardingSession,
  recordOnboardingProgress,
  kickoffSync,
  syncStatus,
  discoveryStatus,
  setBriefingPreference,
  restartDiscovery,
}
